<template>
    <modal @close="onClose" ref="modal">
        <template v-slot:title>Sync From Payroll — {{ company.name }}</template>

        <slot>
            <form-errors :errors="formErrors"/>

            <div v-if="phase == 'form'">
                <p>
                    Press <b>Start</b> to sync this company's employees from Payroll to GetMyPayStub.
                </p>

                <div class="form-row pb-2" v-if="showEmailFieldOverride">
                    <div class="col">
                        <form-input v-model="formData.force_email_field" type="select" label="Payroll Email Field" :options="emailFieldOptions" :errors="errors.force_email_field" />
                    </div>
                </div>
            </div>

            <div v-if="phase == 'running'">
                <p>
                    <i class="far fa-spinner fa-spin"></i>
                    {{ task.status_update || 'Starting' }}…
                </p>

                <div class="progress">
                    <div class="progress-bar" role="progressbar" :style="'width: ' + (task.status_pct || 0) + '%'" :aria-valuenow="task.status_pct || 0" aria-valuemin="0" aria-valuemax="100"></div>
                </div>

                <p class="text-muted small mt-2 mb-0">
                    The sync keeps running if you close this window.
                </p>
            </div>

            <div v-if="phase == 'complete'" class="alert alert-success mb-0">
                <i class="far fa-fw fa-check-circle"></i> Sync from Payroll complete.
            </div>

            <div v-if="phase == 'error'" class="alert alert-danger mb-0">
                <strong>The sync failed.</strong>
                <div v-if="task.error_detail">{{ task.error_detail }}</div>
            </div>
        </slot>

        <template v-slot:footer>
            <div class="form-row" v-if="phase == 'form'">
                <div class="col">
                    <button type="button" class="btn btn-block btn-outline-primary" @click.prevent="close()">Cancel</button>
                </div>
                <div class="col">
                    <button type="submit" class="btn btn-block btn-primary" @click.prevent="onSubmit()" :disabled="submitting">
                        {{ submitting ? 'Starting…' : 'Start' }}
                    </button>
                </div>
            </div>
            <div class="form-row" v-else>
                <div class="col">
                    <button type="button" class="btn btn-block btn-outline-primary" @click.prevent="close()">Close</button>
                </div>
            </div>
        </template>
    </modal>
</template>

<script>
import FormMixin from '@/mixins/Form'
import ModalMixin from '@/mixins/ModalMixin'

// HoursImportTask.HOURS_IMPORT_STATUS_*
const STATUS_ERROR = 30
const STATUS_COMPLETE = 50

export default {
    mixins: [FormMixin, ModalMixin],
    props: ['company'],
    data() {
        return {
            submitting: false,
            taskId: null,
            task: {},
            pollIntervalMs: 3000,
            pollTimer: null,
            formData: this.makeFormData(),
            formRules: {},
            emailFieldOptions: [
                {text: 'Default', value: ''},
                {text: 'Force: ESS', value: 'ess'},
                {text: 'Force: Personal', value: 'personal'},
            ],
        }
    },
    computed: {
        showEmailFieldOverride() {
            return !!(this.company.client && this.company.client.use_personal_email_field)
        },
        phase() {
            if (!this.taskId) {
                return 'form'
            }
            if (this.task.status == STATUS_COMPLETE) {
                return 'complete'
            }
            if (this.task.status == STATUS_ERROR) {
                return 'error'
            }
            return 'running'
        },
    },
    methods: {
        makeFormData() {
            return {
                force_email_field: '',
            }
        },
        reset() {
            this.stopPolling()
            this.errors = {}
            this.formErrors = []
            this.formData = this.makeFormData()
            this.submitting = false
            this.taskId = null
            this.task = {}
        },
        onSubmit() {
            const payload = {company_id: this.company.id}
            if (this.showEmailFieldOverride && this.formData.force_email_field) {
                payload.force_email_field = this.formData.force_email_field
            }

            this.formErrors = []
            this.submitting = true
            return this.$api.post(`/admin/clients/${this.company.client.id}/employee-sync`, payload).then((resp) => {
                this.taskId = resp.task_id
                this.submitting = false
                this.startPolling()
            }).catch((errors) => {
                this.formErrors = errors.__all__ || ['Could not start the sync.']
                this.submitting = false
            })
        },
        startPolling() {
            this.pollOnce()
            this.pollTimer = setInterval(() => this.pollOnce(), this.pollIntervalMs)
        },
        stopPolling() {
            if (this.pollTimer) {
                clearInterval(this.pollTimer)
                this.pollTimer = null
            }
        },
        pollOnce() {
            if (!this.taskId) {
                return
            }
            return this.$api.get(`/admin/clients/${this.company.client.id}/employee-sync/${this.taskId}`).then((resp) => {
                this.task = resp
                if (resp.status == STATUS_COMPLETE || resp.status == STATUS_ERROR) {
                    this.stopPolling()
                }
            }).catch(() => {
                // a failed poll is retried on the next tick
            })
        },
        onClose() {
            this.stopPolling()
            this.$emit('close')
        },
    },
    beforeUnmount() {
        this.stopPolling()
    },
}
</script>
